import React, { Fragment, useState } from 'react';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';
import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import SvgIcon from '@fec/frontend/elements/icons/svg-icon/svg-icon';

import './typeahead.scss';

const Typeahead = (props) => {
  const {
    onChange,
    value,
    placeholder,
    suggestions,
    translations,
    onSuggestionSelected = () => {},
    maxSuggestions = 10,
    onMatchRequest = (query, data, minSearchLength) =>
      defaultMatcher(query, data, minSearchLength),
    onResetRequest = () => handleDefaultReset(),
  } = props;

  const hasValue = value && value.length > 0;

  const [suggestionEntries, setSuggestionEntries] = useState([]);

  const [resultCount, setResultCount] = useState(0);

  // Autosuggest will pass through all these props to the input.
  const inputProps = {
    placeholder,
    value,
    onChange: onChange,
  };

  const getValue = (suggestion) => {
    return suggestion.text;
  };

  const handleDefaultReset = () => {
    onChange({}, { newValue: '' });
  };

  const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const { text } = suggestion;

    if (suggestion.url && suggestion.url.length > 0) {
      return (
        <TypeaheadLink
          url={suggestion.url}
          query={query}
          isHighlighted={isHighlighted}
          text={text}
          matchIndex={suggestion.matchIndex ? suggestion.matchIndex : false}
        />
      );
    }
    return <span>{text}</span>;
  };

  const renderInputComponent = (inputProps, translations) => {
    return (
      <div className="search__input--wrapper typeahead__input-wrapper js-typeahead__input-wrapper">
        <div className="search__icon"></div>
        <label className="search__label">
          <span className="h-offscreen">{translations.inputOffscreenInfo}</span>
          <input
            {...inputProps}
            name="q"
            type="text"
            placeholder={inputProps.placeholder}
            autoComplete="off"
            maxLength="128"
            aria-expanded="true"
          />
        </label>

        <a
          href="#"
          className={classNames('search__close', {
            'typeahead__input-close': hasValue,
          })}
          aria-hidden="true"
          tabIndex="-1"
          title={translations.closeSearchInput}
          onClick={onResetRequest}>
          <SvgIcon styleModifier="svg-icon--close svg-icon--clear-input" />
        </a>
        <button
          type="submit"
          className="h-offscreen"
          tabIndex="-1"
          aria-hidden="true">
          {translations.searchButtonText}
        </button>
      </div>
    );
  };

  const theme = {
    container: 'search-wrapper',
    containerOpen: 'search-wrapper search-wrapper--open search--active',
    input: 'search__input search__input--loaded',
    inputOpen: 'search__input search__input--loaded',
    inputFocused: 'react-autosuggest__input--focused',
    suggestionsContainer: 'search__results',
    suggestionsContainerOpen: 'search__results search__results--open',
    suggestionsList: 'react-autosuggest__suggestions-list',
    suggestion: 'typeahead-suggestion',
    suggestionFirst: 'react-autosuggest__suggestion--first',
    suggestionHighlighted:
      'typeahead-suggestion typeahead-suggestion--highlighted',
    sectionContainer: 'react-autosuggest__section-container',
    sectionContainerFirst: 'react-autosuggest__section-container--first',
    sectionTitle: 'react-autosuggest__section-title',
  };

  const matchedSuggestions = onMatchRequest(value, suggestionEntries, 2);
  if (matchedSuggestions.length !== resultCount) {
    setResultCount(matchedSuggestions.length);
  }

  return (
    <div className="typeahead">
      <span className="h-offscreen" aria-live="polite">
        {resultCount + ' ' + translations.resultCountText}
      </span>
      <Autosuggest
        theme={theme}
        suggestions={matchedSuggestions.slice(0, maxSuggestions)}
        inputProps={inputProps}
        onSuggestionsFetchRequested={() => setSuggestionEntries(suggestions)}
        onSuggestionsClearRequested={() => setSuggestionEntries([])}
        renderSuggestion={renderSuggestion}
        getSuggestionValue={getValue}
        onSuggestionSelected={onSuggestionSelected}
        renderInputComponent={(inputProps) =>
          renderInputComponent(inputProps, translations)
        }
      />
    </div>
  );
};

const TypeaheadLink = ({ text, query, url }) => {
  return (
    <a href={url} className="search-result__link" tabIndex="-1">
      <span role="presentation" aria-hidden={true}>
        <TypeaheadMatchedText text={text} query={query} />
      </span>
      <span className="h-offscreen">{text}</span>
    </a>
  );
};

const TypeaheadMatchedText = ({ text, query }) => {
  const matches = AutosuggestHighlightMatch(text, query);
  const parts = AutosuggestHighlightParse(text, matches);

  return (
    <Fragment>
      {parts.map((part, index) => {
        if (part.highlight) {
          return <strong key={index}>{part.text}</strong>;
        }
        return part.text;
      })}
    </Fragment>
  );
};

export const defaultMatcher = (query, data, minSearchLength) => {
  let results = [];

  if (!data || !query || query.length < minSearchLength) {
    return results;
  }

  data.forEach((item) => {
    const matches = AutosuggestHighlightMatch(item.text, query);
    if (matches && matches.length > 0) {
      results.push({
        text: item.text,
        id: item.id,
        url: item.url ? item.url : null,
        matchSize: matches.length,
      });
    }
  });

  results = results.sort((item1, item2) => {
    if (item1.matchSize > item2.matchSize) {
      return -1;
    }

    if (item1.matchSize < item2.matchSize) {
      return 1;
    }

    return 0;
  });

  return results;
};

export default Typeahead;
